/**
 * Companies cut at discovery, with the reason (E07-F02-T18).
 *
 * The discovery runner drops a candidate BEFORE a research_companies row exists when
 * it is on the request's Do-Not-Source lists (company / industry, E07-F02-T15) or
 * its headcount (as the search reported it) is outside the request's size window.
 * Those cuts used to live only in the server log (`research.discover.dns_excluded`);
 * they are now persisted on research_jobs.discovery_excluded and shown on the
 * request (Review) as a counter + list.
 */
import { z } from 'zod';
/** Why a candidate was cut at discovery. */
export const discoveryExclusionReasonSchema = z.enum([
    'dns_company',
    'dns_industry',
    'size',
    // E06-F01-T10 — the provider industry matches the request's Avoid line.
    'avoid',
    // E07-F02-T22 — the company's HQ is in a US state / Canadian province the request
    // excludes (Excluding locations, e.g. Do-Not-Source States).
    'excluded_state',
    // E07-F07-T03 — the company is on the global Do-Not-Source list (EER clients),
    // snapshotted into the run's criteria (`dnsGlobal`) at start.
    'dns_global',
]);
/** One candidate cut at discovery. */
export const discoveryExclusionSchema = z.object({
    name: z.string(),
    domain: z.string().nullable().default(null),
    reason: discoveryExclusionReasonSchema,
    /** The request value that matched: the DNS entry as typed, or the size window ("50-200"). */
    matched: z.string(),
    /** How it matched: domain / name / industry (DNS), size, or HQ state (excluded_state). */
    matchedBy: z.enum(['domain', 'name', 'industry', 'size', 'state']),
    /** E07-F02-T22 — the HQ state as the search reported it (excluded_state). */
    hqState: z.string().nullable().optional(),
    /** The provider's industry label (dns_industry) — what the DNS entry matched against. */
    industry: z.string().nullable().optional(),
    /** The headcount compared with the window (size) — the whole company's (E07-F02-T19). */
    employees: z.number().nullable().optional(),
    /**
     * E07-F02-T19 — the record was a local office; `employees` is its group's headcount,
     * found under this provider name. Absent = the record's own number.
     */
    groupName: z.string().nullable().optional(),
});
/** Human label of a reason (Review list column). Pure. */
export function discoveryExclusionReasonLabel(reason) {
    switch (reason) {
        case 'dns_company':
            return 'DNS company';
        case 'dns_industry':
            return 'DNS industry';
        case 'size':
            return 'Size';
        case 'avoid':
            return 'Avoid';
        case 'excluded_state':
            return 'Excluded state';
        case 'dns_global':
            return 'EER client (global list)';
    }
}
/** Human sentence for one exclusion (log line / Review list). Pure. */
export function describeDiscoveryExclusion(ex) {
    switch (ex.reason) {
        case 'dns_industry':
            return ex.industry
                ? `Do-not-source industry "${ex.matched}" (company industry: ${ex.industry})`
                : `Do-not-source industry "${ex.matched}"`;
        case 'avoid':
            return ex.industry
                ? `Avoid "${ex.matched}" (company industry: ${ex.industry})`
                : `Avoid "${ex.matched}"`;
        case 'size':
            if (ex.employees == null)
                return `Outside the size window ${ex.matched}`;
            return ex.groupName
                ? `${ex.employees} employees (whole company, ${ex.groupName}) — outside the size window ${ex.matched}`
                : `${ex.employees} employees — outside the size window ${ex.matched}`;
        case 'excluded_state':
            return ex.hqState && ex.hqState !== ex.matched
                ? `HQ in excluded location "${ex.matched}" (reported state: ${ex.hqState})`
                : `HQ in excluded location "${ex.matched}"`;
        case 'dns_global':
            return `EER client on the global do-not-source list "${ex.matched}" (matched by ${ex.matchedBy})`;
        case 'dns_company':
        default:
            return `Do-not-source company "${ex.matched}" (matched by ${ex.matchedBy})`;
    }
}
/**
 * research_jobs.discovery_excluded (JSONB) → validated list. Never throws: a
 * malformed entry is dropped, a non-array (legacy row) is `[]`.
 */
export function parseDiscoveryExclusions(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const item of value) {
        const parsed = discoveryExclusionSchema.safeParse(item);
        if (parsed.success)
            out.push(parsed.data);
    }
    return out;
}
//# sourceMappingURL=research-discovery-exclusions.js.map